'use client';

import React, { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { X, Wallet, Check } from 'lucide-react';
import { PokerSiteLogo } from '../Dashboard/PokerSiteLogo';
import { cn } from '@/lib/utils';
import { UniverseBadge } from '../ui/universe-badge';

interface Bankroll {
  id: string;
  siteName: string;
  type: string;
  currentBalance: string;
  currency: string;
}

interface SelectBankrollModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (bankrollId: string, currency: string) => void;
}

export const SelectBankrollModal: React.FC<SelectBankrollModalProps> = ({
  isOpen,
  onClose,
  onSelect,
}) => {
  const t = useTranslations('bankrolls.detail.transactions');
  const [bankrolls, setBankrolls] = useState<Bankroll[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBankrollId, setSelectedBankrollId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      const fetchBankrolls = async () => {
        try {
          setIsLoading(true);
          const response = await fetch('/api/bankrolls');
          if (response.ok) {
            const data = await response.json();
            setBankrolls(data.bankrolls || []);
          }
        } catch (error) {
          console.error('Error fetching bankrolls:', error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchBankrolls();
      setSelectedBankrollId(null);
    }
  }, [isOpen]);

  const handleSelect = () => {
    if (selectedBankrollId) {
      const bankroll = bankrolls.find((b) => b.id === selectedBankrollId);
      if (bankroll) {
        onSelect(bankroll.id, bankroll.currency);
      }
    }
  };

  if (!isOpen) return null;

  const formatCurrency = (amount: string, currency: string = 'EUR') => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency,
      currencyDisplay: 'symbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(parseFloat(amount));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-50 w-full max-w-md mx-4 bg-white dark:bg-gray-800 rounded-lg shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t('selectBankroll.title')}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {t('selectBankroll.subtitle')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[400px] overflow-y-auto">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-2"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : bankrolls.length === 0 ? (
            <div className="text-center py-8">
              <Wallet className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('selectBankroll.noBankrolls')}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {bankrolls.map((bankroll) => (
                <button
                  key={bankroll.id}
                  onClick={() => setSelectedBankrollId(bankroll.id)}
                  className={cn(
                    'w-full flex items-center justify-between p-4 rounded-lg border transition-all',
                    'hover:bg-gray-50 dark:hover:bg-gray-700/50',
                    selectedBankrollId === bankroll.id
                      ? 'border-gold bg-gold/5 dark:bg-gold/10'
                      : 'border-gray-200 dark:border-gray-700'
                  )}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <PokerSiteLogo
                      siteName={bankroll.siteName}
                      size={48}
                      className="w-12 h-12 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-gray-900 dark:text-white text-base truncate">
                          {bankroll.siteName}
                        </p>
                        {(bankroll.type === 'Online' || bankroll.type === 'Live') ? (
                          <UniverseBadge universe={bankroll.type as 'Online' | 'Live'} />
                        ) : (
                          <span className="px-2 py-0.5 text-xs rounded-full flex-shrink-0 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                            {bankroll.type}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                        {formatCurrency(bankroll.currentBalance, bankroll.currency)}
                      </p>
                    </div>
                  </div>
                  {selectedBankrollId === bankroll.id && (
                    <Check className="w-5 h-5 text-gold flex-shrink-0 ml-2" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {bankrolls.length > 0 && (
          <div className="flex items-center gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {t('selectBankroll.cancel')}
            </button>
            <button
              onClick={handleSelect}
              disabled={!selectedBankrollId}
              className={cn(
                'flex-1 px-4 py-2 rounded-lg font-semibold transition-colors',
                selectedBankrollId
                  ? 'bg-gradient-to-r from-[#1a2332] via-[#0f172a] to-[#0c1220] hover:opacity-90 text-white'
                  : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              )}
            >
              {t('selectBankroll.continue')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

